import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, exhaustMap, of, switchMap } from "rxjs";
import { selectCashieringTransactions, selectInfo, selectRecurringTransactions } from "../../account-info-header/reducers";
import { CancelTransactionDetails } from "../../shared/models/CancelTransactionDetails";
import { StatusViewService } from "../../shared/services/status-view.service";
import { TransactionsTableActions } from "../actions";
import { CashieringTransaction, RecurringTransaction } from "../models";
import { concatLatestFrom } from "@ngrx/operators";

@Injectable()
export class TransactionTableEffects {
    constructor(
        private actions$: Actions,
        private statusViewService: StatusViewService, 
        private store: Store
    ) { }

    callCancelTransaction$ = createEffect(() => 
        this.actions$.pipe(
            ofType(TransactionsTableActions.cancelTransaction),
            concatLatestFrom(() => [
                this.store.select(selectCashieringTransactions),
                this.store.select(selectRecurringTransactions),
                this.store.select(selectInfo)
            ]),
            exhaustMap(([action, cashieringTransactions, recurringTransactions, accountInfo]) => {
                return this.statusViewService.cancelTransaction(
                    this.mapCancelRequest(
                        action?.CashieringRqstId ?? '', 
                        cashieringTransactions, 
                        recurringTransactions, 
                        accountInfo.accountId,
                        action.IsActiveRecurring))
                    .pipe(
                        switchMap(() => {
                            return of(TransactionsTableActions.cancelTransactionSuccess({ cashieringRequestId: action?.CashieringRqstId ?? '' }));
                        }),
                        catchError(ex => { 
                            return of(TransactionsTableActions.cancelTransactionFailure({
                                errorMsg: ex.error[0]?.Detail ?? ex.message
                            }));
                        })
                )
            })
        )
    );

    mapCancelRequest(
        cashieringReqId: string | number, 
        cashieringTransactions: CashieringTransaction[], 
        recurringTransactions: RecurringTransaction[], 
        accountId: string,
        isActiveRecurring: boolean
    ) : CancelTransactionDetails {
        let tran: CashieringTransaction | RecurringTransaction | undefined;
        let transactionType: 'JournalRecurring' | 'JournalSloa' | 'JournalOneTime' | '';
        if(isActiveRecurring) {
            tran = this.getMatchingRecurringTransaction(cashieringReqId, recurringTransactions);
            if (!tran) { 
                throw `Couldn't find transaction by cashiering request id ${cashieringReqId}`; 
            }
            transactionType = tran.mediaTypeCd === 'J' ? 'JournalRecurring' : '';
        }
        else {
            tran = this.getMatchingNonRecurringTransaction(cashieringReqId, cashieringTransactions);
            if (!tran) { 
                throw `Couldn't find transaction by cashiering request id ${cashieringReqId}`; 
            }
            transactionType = tran.mediaTypeCd === 'J'
                ? tran.isRecurring ? 'JournalRecurring' : tran.isSloaSetup ? 'JournalSloa' : 'JournalOneTime'
                : '';
        }
        let mediaTypeCd: string = tran.mediaTypeCd === 'J' || tran.mediaTypeCd === 'C' ? tran.mediaTypeCd : tran.txnCode ?? '';
        return {
            cancelRequest: {
                cancelReasonCode: '',
                cashieringRequestId: tran.cashieringRqstId.toString() ?? '',
                cashieringSecRequestId: tran.cashieringRqstSecondId,
                masterAccountId: tran.faMasterAcct,
                mediaTypeCd: mediaTypeCd,
                subAccountId: accountId,
                transactionType: transactionType
            }
        };
    }

    getMatchingRecurringTransaction(cashieringReqId: string | number, transactions: RecurringTransaction[]): RecurringTransaction | undefined{
        return transactions.find(tran => tran.cashieringRqstId == cashieringReqId);
    }

    getMatchingNonRecurringTransaction(cashieringReqId: string | number, transactions: CashieringTransaction[]): CashieringTransaction | undefined {
        return transactions.find(tran => tran.cashieringRqstId == cashieringReqId);
    }
}